import ApisServiceApp from './ApisServiceApp'
import { apiRequest } from '@/utils/apiRequestUtils'
import { banHangListParamsFromQueryString } from '@/utils/banHangListQueryUtils'
import { normalizeDetailResponse, normalizeListResponse, type PagedListResult } from '@/utils/apiResponseUtils'

const PREFIX = '/dat-hang'

const blobResponseConfig = { responseType: 'blob' as const }

const parseBlobErrorIfJson = async (blob: Blob): Promise<never> => {
  const text = await blob.text()
  try {
    const j = JSON.parse(text) as { message?: string }
    throw new Error(j?.message || 'Yeu cau that bai')
  } catch (e) {
    if (e instanceof SyntaxError) throw new Error(text.trim().slice(0, 240) || 'Yeu cau that bai')
    throw e
  }
}

const ensureExcelBlob = async (blob: Blob): Promise<Blob> => {
  if (blob.type && blob.type.includes('application/json')) await parseBlobErrorIfJson(blob)
  return blob
}

export const fetchDatHangList = (search: string): Promise<PagedListResult<Record<string, unknown>>> => {
  const params = banHangListParamsFromQueryString(search)
  return apiRequest(
    ApisServiceApp.get(`${PREFIX}/danh-sach-dat-hang`, { params }).then((res: unknown) => {
      const { data, paging, summary } = normalizeListResponse(res as Parameters<typeof normalizeListResponse>[0])
      return { data: data as Record<string, unknown>[], paging, summary }
    }),
  )
}

export type DatHangChiTietHangRow = {
  id: string
  datHangId: string
  maDatHang: string
  thoiGian: string
  trangThai: string
  chiNhanhId: number | null
  tenChiNhanh: string
  nguoiBan: string
  khachHangId: string | null
  maKhachHang: string
  tenKhachHang: string
  hangHoaId: string
  maHangHoa: string
  tenHangHoa: string
  donViTinh: string | null
  soLuong: number
  soLuongDaXuat: number
  tongCanNang: number
  tongTheTich: number
  donGia: number
  giamGia: number
  giaBan: number
  thanhTien: number
  giamGiaChung: number
  thuKhacChung: number
  tongCong: number
  ghiChu: string | null
}

export type DatHangChiTietHangSummary = {
  soSanPham: number
  soLuong: number
  tongCanNang: number
  tongTheTich: number
  giamGia: number
  thanhTien: number
  giamGiaChung: number
  thuKhacChung: number
  tongCong: number
}

/** Danh sách chi tiết hàng đặt hàng — cùng bộ lọc danh sách đặt hàng. */
export const fetchDatHangChiTietHangList = async (
  search: string,
): Promise<
  Omit<PagedListResult<DatHangChiTietHangRow>, 'summary'> & { summary: DatHangChiTietHangSummary | null }
> => {
  const params = banHangListParamsFromQueryString(search)
  const res = (await apiRequest(
    ApisServiceApp.get(`${PREFIX}/danh-sach-chi-tiet-hang-dat-hang`, { params }),
  )) as Parameters<typeof normalizeListResponse>[0] & { summary?: DatHangChiTietHangSummary }
  const { data, paging } = normalizeListResponse(res)
  return {
    data: data as DatHangChiTietHangRow[],
    paging,
    summary: (res as { summary?: DatHangChiTietHangSummary })?.summary ?? null,
  }
}

export const fetchMaDatHangMoi = () =>
  apiRequest(ApisServiceApp.get(`${PREFIX}/ma-dat-hang-moi`))

export const xuatDatHangTongQuanBlob = async (search?: string, ids?: string[]): Promise<Blob> => {
  const params = search ? banHangListParamsFromQueryString(search) : {}
  if (ids?.length) (params as Record<string, unknown>).ids = ids.join(',')
  const blob = (await apiRequest(
    ApisServiceApp.get<Blob>(`${PREFIX}/xuat-file-tong-quan-dat-hang`, { ...blobResponseConfig, params }),
  )) as unknown as Blob
  return ensureExcelBlob(blob)
}

/** Chi tiết đủ cột. Truyền `ids` → chỉ xuất các phiếu đã chọn (bỏ lọc URL). */
export const xuatDatHangChiTietBlob = async (search?: string, ids?: string[]): Promise<Blob> => {
  const params = ids?.length
    ? ({ ids: ids.join(',') } as Record<string, unknown>)
    : search
      ? banHangListParamsFromQueryString(search)
      : {}
  const blob = (await apiRequest(
    ApisServiceApp.get<Blob>(`${PREFIX}/xuat-file-chi-tiet-dat-hang`, { ...blobResponseConfig, params }),
  )) as unknown as Blob
  return ensureExcelBlob(blob)
}

export const taoDatHang = (body: Record<string, unknown>) =>
  apiRequest(ApisServiceApp.post(`${PREFIX}/tao-dat-hang`, body))

export const taiTemplateImportDatHangBlob = async (): Promise<Blob> => {
  const blob = (await apiRequest(
    ApisServiceApp.get<Blob>(`${PREFIX}/template-import-dat-hang`, blobResponseConfig),
  )) as unknown as Blob
  return ensureExcelBlob(blob)
}

/** FE parse Excel → POST JSON. */
export function nhapDanhSachDatHang(rows: unknown[]) {
  return apiRequest(ApisServiceApp.post(`${PREFIX}/dat-hang-nhap-danh-sach`, rows))
}

export const fetchDatHangDetail = (id: string | number) =>
  apiRequest(
    ApisServiceApp.get(`${PREFIX}/${id}/chi-tiet-dat-hang`).then((res: unknown) => ({
      data: normalizeDetailResponse(res as { data?: Record<string, unknown> }),
    })),
  )

export const capNhatDatHang = (id: string | number, body: Record<string, unknown>) =>
  apiRequest(ApisServiceApp.put(`${PREFIX}/${id}/cap-nhat-dat-hang`, body))

/** Sửa tại chỗ ghi chú / kênh bán / người bán / giao hàng — không đụng chi tiết, thanh toán. */
export const capNhatThongTinDatHang = (id: string | number, body: Record<string, unknown>) =>
  apiRequest(ApisServiceApp.patch(`${PREFIX}/${id}/cap-nhat-thong-tin-dat-hang`, body))

export const xoaDatHang = (id: string | number) =>
  apiRequest(ApisServiceApp.delete(`${PREFIX}/${id}/xoa-dat-hang`))

export const chuyenTrangThaiDatHang = (id: string | number, body: Record<string, unknown>) =>
  apiRequest(ApisServiceApp.patch(`${PREFIX}/${id}/chuyen-trang-thai-dat-hang`, body))

export const saoChepDatHang = (id: string | number) =>
  apiRequest(ApisServiceApp.post(`${PREFIX}/${id}/sao-chep-dat-hang`, {}))

export const danhDauYeuThichDatHang = (id: string | number, body?: Record<string, unknown>) =>
  apiRequest(ApisServiceApp.patch(`${PREFIX}/${id}/danh-dau-yeu-thich-dat-hang`, body ?? {}))

export const chotDatHangThanhHoaDon = (id: string | number) =>
  apiRequest(ApisServiceApp.post(`${PREFIX}/${id}/chot-thanh-hoa-don`, {}))

// ============================================================
// PHƯƠNG ÁN C: 1 đơn hàng → nhiều hóa đơn
// ============================================================

export type ChiTietXuatHoaDonPayload = {
  datHangChiTietId: string
  soLuong: number
  ghiChu?: string
}

export type TaoHoaDonTuDatHangPayload = {
  chiTiet: ChiTietXuatHoaDonPayload[]
  loaiHoaDon?: string
  thoiGian?: string
  ghiChu?: string
  giamGia?: number
  giamGiaPhanTram?: number
  thueSuat?: number
  ghiChuLink?: string
}

/** Tạo 1 hóa đơn con từ đơn hàng mẹ (chọn dòng + số lượng xuất). */
export const taoHoaDonTuDatHang = (id: string | number, body: TaoHoaDonTuDatHangPayload) =>
  apiRequest(ApisServiceApp.post(`${PREFIX}/${id}/tao-hoa-don-tu-dat-hang`, body))

/** Tiến độ xuất hóa đơn của đơn hàng (per-line + summary + danh sách HĐ con). */
export const fetchTienDoXuatHoaDon = (id: string | number) =>
  apiRequest(ApisServiceApp.get(`${PREFIX}/${id}/tien-do-xuat-hoa-don`))

/** Danh sách các hóa đơn con đã xuất từ đơn hàng. */
export const fetchDanhSachHoaDonCon = (id: string | number) =>
  apiRequest(ApisServiceApp.get(`${PREFIX}/${id}/danh-sach-hoa-don-con`))
